import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../Layout";
import apiFetch from "../../utils/apiFetch";
import "./entitiesList.scss";

const getEntityCopy = (entityType) => {
  if (entityType === "substation") {
    return {
      modifier: "substation",
      kicker: "Инфратузилма паспорти",
      title: "Подстанциялар реестри",
      lead: "Филиаллар кесимидаги подстанциялар, паспортга ўтиш ва охирги ҳисобот ҳолати.",
      typeLabel: "Подстанция",
      reportLabel: "Охирги ҳисобот",
      emptyReport: "Ҳисобот йўқ",
      searchPlaceholder: "Подстанция номи ёки ҳисобот бўйича қидириш",
    };
  }

  return {
    modifier: "network",
    kicker: "Инфратузилма паспорти",
    title: "110-500 кВ электр узатиш ҳаво тармоқлари",
    lead: "ЭУХТ реестри, паспорт саҳифалари ва охирги ҳисоботлар тез кўриш учун.",
    typeLabel: "ЭУХТ",
    reportLabel: "Охирги ҳисобот",
    emptyReport: "Ҳисобот йўқ",
    searchPlaceholder: "Тармоқ номи ёки ҳисобот бўйича қидириш",
  };
};

const normalizeText = (value) => String(value ?? "").toLowerCase();

const EntitiesList = ({ entityType, apiEndpoint, reportType }) => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const copy = useMemo(() => getEntityCopy(entityType), [entityType]);

  useEffect(() => {
    let isCancelled = false;

    async function fetchData() {
      setLoading(true);
      setError("");

      try {
        const entitiesResponse = await apiFetch(apiEndpoint, { cache: "no-store" });
        if (!entitiesResponse.ok) {
          throw new Error(`Ошибка при получении данных: ${entitiesResponse.status}`);
        }
        const entitiesData = await entitiesResponse.json();

        const formsResponse = await apiFetch("/api/forms", { cache: "no-store" });
        if (!formsResponse.ok) {
          throw new Error(`Ошибка при получении форм: ${formsResponse.status}`);
        }
        const formsData = await formsResponse.json();
        const forms = Array.isArray(formsData) ? formsData : [];

        const enrichedEntities = (Array.isArray(entitiesData) ? entitiesData : []).map(
          (entity) => {
            const relatedForms = forms.filter(
              (form) => form[reportType] === entity.id && form.type === entityType
            );

            const lastForm =
              relatedForms.length > 0
                ? relatedForms.reduce((latest, current) =>
                    new Date(latest.completionDate) > new Date(current.completionDate)
                      ? latest
                      : current
                  )
                : null;

            return {
              ...entity,
              lastForm,
            };
          }
        );

        if (!isCancelled) {
          setEntities(enrichedEntities);
        }
      } catch (fetchError) {
        console.error("Ошибка загрузки данных:", fetchError);
        if (!isCancelled) {
          setError("Маълумотларни юклашда хато юз берди.");
          setEntities([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [apiEndpoint, entityType, reportType]);

  const filteredEntities = useMemo(() => {
    const query = normalizeText(search).trim();
    if (!query) return entities;

    return entities.filter((entity) => {
      const haystack = [
        entity.name,
        entity.slug,
        entity.lastForm?.description,
        entity.lastForm?.completionDate,
      ]
        .map(normalizeText)
        .join(" ");

      return haystack.includes(query);
    });
  }, [entities, search]);

  const reportsCount = useMemo(
    () => entities.filter((entity) => Boolean(entity.lastForm)).length,
    [entities]
  );

  const hasSearch = search.trim().length > 0;

  return (
    <Layout>
      <section className={`entities entities--${copy.modifier}`}>
        <div className="container">
          <div className="entities-shell">
            <header className="entities-hero">
              <div>
                <p className="entities-hero__kicker">{copy.kicker}</p>
                <h1 className="entities-hero__title">{copy.title}</h1>
                <p className="entities-hero__lead">{copy.lead}</p>
              </div>

              <div className="entities-hero__stats" aria-label="Реестр ҳолати">
                <div className="entities-stat">
                  <span>Жами</span>
                  <strong>{entities.length}</strong>
                </div>
                <div className="entities-stat entities-stat--ok">
                  <span>Ҳисоботли</span>
                  <strong>{reportsCount}</strong>
                </div>
              </div>
            </header>

            <div className="entities-toolbar">
              <label className="entities-search">
                <span className="entities-search__label">Қидириш</span>
                <input
                  className="entities-search__input"
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={copy.searchPlaceholder}
                />
              </label>

              {hasSearch && (
                <button
                  className="entities-toolbar__clear"
                  type="button"
                  onClick={() => setSearch("")}
                >
                  Тозалаш
                </button>
              )}
            </div>

            {loading && (
              <div className="entities-state entities-state--loading">
                Маълумотлар юкланмоқда...
              </div>
            )}

            {!loading && error && (
              <div className="entities-state entities-state--error">{error}</div>
            )}

            {!loading && !error && entities.length === 0 && (
              <div className="entities-state entities-state--empty">
                Реестрда маълумот топилмади.
              </div>
            )}

            {!loading && !error && entities.length > 0 && filteredEntities.length === 0 && (
              <div className="entities-state entities-state--empty">
                Қидирув бўйича натижа топилмади.
              </div>
            )}

            {!loading && !error && filteredEntities.length > 0 && (
              <ul className="entities-list" aria-label={copy.title}>
                {filteredEntities.map((entity) => (
                  <li key={entity.id} className="entities-list__item">
                    <Link
                      className="entities-card"
                      to={`/indicators/${entityType}s/${entity.slug}`}
                    >
                      <div className="entities-card__main">
                        <div className="entities-card__topline">
                          <span className="entities-card__type">{copy.typeLabel}</span>
                          <span
                            className={`entities-card__status ${
                              entity.lastForm ? "is-ready" : "is-empty"
                            }`}
                          >
                            {entity.lastForm ? "Ҳисобот бор" : copy.emptyReport}
                          </span>
                        </div>

                        <h2 className="entities-card__name">{entity.name}</h2>

                        <p className="entities-card__report">
                          {entity.lastForm ? (
                            <>
                              <span>{copy.reportLabel}:</span>{" "}
                              {entity.lastForm.description || "Тавсифсиз ҳисобот"}
                            </>
                          ) : (
                            copy.emptyReport
                          )}
                        </p>
                      </div>

                      <span className="entities-card__action">Паспорт</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default EntitiesList;
